<template>
  <div
    data-component="base-common-content"
    :class="state"
  >
    <div
      v-if="useSearch || !!$slots['search']"
      class="header"
    >
      <slot name="search" />

      <base-search-input
        v-if="useSearch && !$slots['search']"
        :search="search"
        :placeholder="placeholder"
        :size="size"
        :is-disabled="isLoading"
        @search-change="$emit('search-change', $event)"
      />
    </div>

    <div
      class="content"
      :class="{ 'is-loading': isLoading || isSearching }"
    >
      <or-progress
        v-if="isLoading || isSearching"
        type="circle"
        loading
      />

      <template v-else>
        <slot name="append" />
        <slot name="default" />

        <template v-if="canShowOptions">
          <div
            v-for="option of options"
            :key="option[dataKey]"
            class="option-wrapper"
          >
            <slot
              name="option"
              :option="option"
            />
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue-demi';
import { OrProgressV3 as OrProgress } from '@onereach/ui-components-25';

import BaseSearchInput from './BaseSearchInput.vue';
import { ContentOption } from '../../types';
import { DEFAULT_DATA_KEY, SIZE_PROP } from '../../constants';

export default defineComponent({
  name: 'BaseCommonContent',
  components: {
    OrProgress,
    BaseSearchInput,
  },
  props: {
    options: {
      type: Array as PropType<ContentOption[]>,
      default: () => [],
    },
    dataKey: {
      type: String,
      default: DEFAULT_DATA_KEY,
    },
    search: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    useSearch: {
      type: Boolean,
      default: true,
    },
    size: SIZE_PROP,
    showOptions: {
      type: Boolean,
      default: true,
    },
    isSearching: Boolean,
    isLoading: Boolean,
  },
  computed: {
    state() {
      return { [`is-${this.size}`]: true };
    },
    canShowOptions() {
      return this.showOptions && !!this.options.length;
    },
  },
});
</script>

<style lang="scss" scoped>
[data-component='base-common-content'] {
  display: flex;
  flex-direction: column;
  gap: var(--s-4);
  height: 100%;
  overflow: hidden;

  .header {
    display: flex;
    flex: 0 0 auto;
    padding: 0 var(--s-5);

    [data-component='base-search-input'] {
      flex: 1 1 100%;
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    flex: 1 1 100%;
    overflow: auto;

    &.is-loading {
      align-items: center;
      justify-content: center;
    }
  }
}
</style>
